import { useState, useMemo } from "react";
import { OrgPicker } from "@/components/OrgPicker";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, Link2, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/toaster";
import * as api from "@/services/api";
import type { PermissionAction } from "@/types";
import {
	DependencyWarningDialog,
	useDependencyCheck,
} from "@/components/features/DependencyWarningDialog";

// Action colors
const actionColors: Record<string, string> = {
	view: "text-blue-600",
	create: "text-green-600",
	update: "text-yellow-600",
	delete: "text-red-600",
	approve: "text-purple-600",
	export: "text-gray-600",
	manage: "text-indigo-600",
};

interface PendingToggle {
	roleId: string;
	featureCode: string;
	action: string;
	isEnabling: boolean;
}

export function PermissionMatrix() {
	const [selectedOrgId, setSelectedOrgId] = useState<string>("__all__");
	const [search, setSearch] = useState("");
	const [selectedModule, setSelectedModule] = useState<string | null>(null);
	const queryClient = useQueryClient();

	// Dialog state
	const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogMode, setDialogMode] = useState<"enable" | "disable">("enable");
	const [pendingToggle, setPendingToggle] = useState<PendingToggle | null>(null);
	const [autoGrantedDeps, setAutoGrantedDeps] = useState<
		api.AutoGrantedPermission[]
	>([]);
	const [affectedDependents, setAffectedDependents] = useState<
		Array<{ feature: string; featureName: string; reason?: string }>
	>([]);

	const { data: orgs } = useQuery({
		queryKey: ["organizations"],
		queryFn: api.getOrganizations,
	});

	const { data: matrix, isLoading } = useQuery({
		queryKey: ["permission-matrix", selectedOrgId],
		queryFn: () =>
			api.getPermissionMatrix(
				selectedOrgId === "__all__" ? undefined : selectedOrgId,
			),
	});

	// Fetch dependency information
	const { data: dependencyInfo } = useQuery({
		queryKey: ["features-dependency-info"],
		queryFn: api.getFeaturesWithDependencyInfo,
	});

	const toggleMutation = useMutation({
		mutationFn: api.togglePermission,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["permission-matrix", selectedOrgId],
			});
		},
		onError: (error) => {
			toast({
				title: "Failed to toggle permission",
				description: String(error),
				variant: "destructive",
			});
		},
	});

	// Dependency check hook
	const dependencyCheck = useDependencyCheck({
		features: dependencyInfo?.features || [],
		dependencyMap: dependencyInfo?.dependencyMap || {},
		reverseDependencyMap: dependencyInfo?.reverseDependencyMap || {},
		enabledFeatures: [], // We check per-role, so this is dynamically calculated
	});

	// Get unique modules
	const modules = useMemo(() => {
		if (!matrix) return [];
		const mods = new Set(matrix.features.map((f) => f.module));
		return Array.from(mods).sort();
	}, [matrix]);

	// Filter features
	const filteredFeatures = useMemo(() => {
		if (!matrix) return [];
		return matrix.features.filter((f) => {
			const featureCode = (f as any).code || f.slug;
			const featureLabel = (f as any).name || f.ui?.label || featureCode;
			const matchesSearch =
				!search ||
				featureCode.toLowerCase().includes(search.toLowerCase()) ||
				featureLabel.toLowerCase().includes(search.toLowerCase());
			const matchesModule = !selectedModule || f.module === selectedModule;
			// Skip section headers
			const isNotSection = !f.ui?.isSection;
			return matchesSearch && matchesModule && isNotSection;
		});
	}, [matrix, search, selectedModule]);

	// Get enabled features for a specific role
	const getRoleEnabledFeatures = (roleId: string): string[] => {
		if (!matrix) return [];
		const roleMatrix = matrix.matrix[roleId] || {};
		return Object.entries(roleMatrix)
			.filter(([_, actions]) => actions && actions.length > 0)
			.map(([featureCode]) => featureCode);
	};

	// Check if enabling a permission requires auto-granting dependencies
	const checkEnableDependencies = async (
		roleId: string,
		featureCode: string,
		action: string,
	) => {
		const enabledFeatures = getRoleEnabledFeatures(roleId);
		const deps = dependencyInfo?.dependencyMap[featureCode.toLowerCase()] || [];

		// Filter to only show dependencies that are not yet enabled
		const missingDeps = deps.filter(
			(dep) =>
				!enabledFeatures.some(
					(f) => f.toLowerCase() === dep.feature.toLowerCase(),
				),
		);

		if (missingDeps.length > 0) {
			setAutoGrantedDeps(
				missingDeps.map((dep) => ({
					feature: dep.feature,
					action: dep.action,
					requiredBy: featureCode,
					reason: dep.reason,
				})),
			);
			setDialogMode("enable");
			setPendingToggle({ roleId, featureCode, action, isEnabling: true });
			setDialogOpen(true);
			return true;
		}
		return false;
	};

	// Check if disabling a permission affects dependent features
	const checkDisableDependents = (
		roleId: string,
		featureCode: string,
		action: string,
	) => {
		const enabledFeatures = getRoleEnabledFeatures(roleId);
		const dependentCodes =
			dependencyInfo?.reverseDependencyMap[featureCode.toLowerCase()] || [];

		// Filter to only show dependents that are currently enabled
		const enabledDependents = dependentCodes
			.filter((depCode) =>
				enabledFeatures.some((f) => f.toLowerCase() === depCode.toLowerCase()),
			)
			.map((depCode) => {
				const depFeatureDeps =
					dependencyInfo?.dependencyMap[depCode.toLowerCase()] || [];
				const reasonDep = depFeatureDeps.find(
					(d) => d.feature.toLowerCase() === featureCode.toLowerCase(),
				);
				return {
					feature: depCode,
					featureName: dependencyCheck.getFeatureName(depCode),
					reason: reasonDep?.reason,
				};
			});

		if (enabledDependents.length > 0) {
			setAffectedDependents(enabledDependents);
			setDialogMode("disable");
			setPendingToggle({ roleId, featureCode, action, isEnabling: false });
			setDialogOpen(true);
			return true;
		}
		return false;
	};

	const handleToggle = async (
		roleId: string,
		featureCode: string,
		action: string,
	) => {
		const isCurrentlyEnabled = hasAction(roleId, featureCode, action);

		if (!isCurrentlyEnabled) {
			// Enabling - check for dependencies
			const hasDeps = await checkEnableDependencies(
				roleId,
				featureCode,
				action,
			);
			if (hasDeps) return; // Dialog will handle the action
		} else {
			// Disabling - check if any enabled features depend on this
			// Only check if we're removing the last action (especially 'view')
			if (action === "view") {
				const hasDependents = checkDisableDependents(
					roleId,
					featureCode,
					action,
				);
				if (hasDependents) return; // Dialog will handle the action
			}
		}

		// No dependencies/dependents, proceed directly
		toggleMutation.mutate({
			roleId,
			featureCode,
			action,
			organizationId: selectedOrgId === "__all__" ? undefined : selectedOrgId,
		});
	};

	const handleDialogConfirm = () => {
		if (!pendingToggle) return;

		// Toggle the original permission
		toggleMutation.mutate({
			roleId: pendingToggle.roleId,
			featureCode: pendingToggle.featureCode,
			action: pendingToggle.action,
			organizationId: selectedOrgId === "__all__" ? undefined : selectedOrgId,
		});

		// If enabling, also enable dependencies
		if (pendingToggle.isEnabling && autoGrantedDeps.length > 0) {
			for (const dep of autoGrantedDeps) {
				// Check if not already enabled
				if (!hasAction(pendingToggle.roleId, dep.feature, dep.action)) {
					toggleMutation.mutate({
						roleId: pendingToggle.roleId,
						featureCode: dep.feature,
						action: dep.action,
						organizationId:
							selectedOrgId === "__all__" ? undefined : selectedOrgId,
					});
				}
			}
		}

		// Reset dialog state
		setDialogOpen(false);
		setPendingToggle(null);
		setAutoGrantedDeps([]);
		setAffectedDependents([]);
	};

	const handleDialogCancel = () => {
		setDialogOpen(false);
		setPendingToggle(null);
		setAutoGrantedDeps([]);
		setAffectedDependents([]);
	};

	const hasAction = (
		roleId: string,
		featureCode: string,
		action: string,
	): boolean => {
		// Matrix structure: roleId -> featureCode -> actions[]
		return (
			matrix?.matrix[roleId]?.[featureCode]?.includes(
				action as PermissionAction,
			) || false
		);
	};

	// Check if a feature has dependencies or dependents
	const getFeatureDependencyInfo = (featureCode: string) => {
		const code = featureCode.toLowerCase();
		const deps = dependencyInfo?.dependencyMap[code] || [];
		const dependents = dependencyInfo?.reverseDependencyMap[code] || [];
		return {
			hasDependencies: deps.length > 0,
			dependencyCount: deps.length,
			hasDependents: dependents.length > 0,
			dependentCount: dependents.length,
		};
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-3xl font-bold text-gray-900">Permission Matrix</h1>
				<p className="text-gray-600 mt-2">
					Configure role permissions for features
				</p>
			</div>

			{/* Filters */}
			<Card>
				<CardContent className="pt-4">
					<div className="flex flex-wrap gap-4">
						<div className="flex-1 min-w-[200px]">
							<OrgPicker
								orgs={orgs}
								value={selectedOrgId}
								onChange={setSelectedOrgId}
								allowGlobal
							/>
						</div>

						<div className="relative flex-1 min-w-[200px]">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
							<Input
								placeholder="Search features..."
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								className="pl-10"
							/>
						</div>

						<div className="flex gap-2 flex-wrap">
							<Button
								variant={selectedModule === null ? "default" : "outline"}
								size="sm"
								onClick={() => setSelectedModule(null)}
							>
								All
							</Button>
							{modules.map((module) => (
								<Button
									key={module}
									variant={selectedModule === module ? "default" : "outline"}
									size="sm"
									onClick={() => setSelectedModule(module)}
								>
									{module}
								</Button>
							))}
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Dependency Info Banner */}
			{dependencyInfo && (
				<Card className="border-blue-200 bg-blue-50">
					<CardContent className="py-3">
						<div className="flex items-center gap-2 text-sm text-blue-700">
							<Link2 className="w-4 h-4" />
							<span>
								<strong>Dependency Validation Active:</strong> When enabling
								permissions, required dependencies will be automatically
								suggested. When disabling, you'll be warned about affected
								features.
							</span>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Matrix */}
			<Card>
				<CardContent className="pt-4 overflow-x-auto">
					<table className="w-full">
						<thead>
							<tr className="border-b">
								<th className="text-left py-3 px-4 font-medium text-sm sticky left-0 bg-white z-10 min-w-[200px]">
									Feature
								</th>
								{matrix?.roles.map((role) => (
									<th
										key={role._id}
										className="text-center py-3 px-2 font-medium text-sm min-w-[140px]"
									>
										<div className="flex flex-col items-center gap-1">
											<span>{role.displayName || role.name}</span>
											<div className="flex gap-1">
												{role.isSystem && (
													<Badge variant="secondary" className="text-[10px]">
														System
													</Badge>
												)}
												{role.isGlobal && (
													<Badge variant="outline" className="text-[10px]">
														Global
													</Badge>
												)}
											</div>
										</div>
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{filteredFeatures.map((feature) => {
								const featureCode = (feature as any).code || feature.slug;
								const featureLabel =
									(feature as any).name || feature.ui?.label || featureCode;
								const depInfo = getFeatureDependencyInfo(featureCode);

								return (
									<tr
										key={featureCode}
										className="border-b hover:bg-gray-50"
									>
										<td className="py-3 px-4 sticky left-0 bg-white">
											<div>
												<div className="flex items-center gap-2">
													<span className="font-medium text-sm">
														{featureLabel}
													</span>
															{depInfo.hasDependencies && (
														<Badge
															variant="outline"
															className="text-[10px] border-blue-300 text-blue-600 cursor-help"
															title={`Requires ${depInfo.dependencyCount} other feature(s)`}
														>
															<Link2 className="w-3 h-3 mr-1" />
															{depInfo.dependencyCount}
														</Badge>
													)}
													{depInfo.hasDependents && (
														<Badge
															variant="outline"
															className="text-[10px] border-yellow-300 text-yellow-600 cursor-help"
															title={`${depInfo.dependentCount} feature(s) depend on this`}
														>
															<AlertTriangle className="w-3 h-3 mr-1" />
															{depInfo.dependentCount}
														</Badge>
													)}
												</div>
												<p className="text-xs text-muted-foreground font-mono">
													{featureCode}
												</p>
											</div>
										</td>
										{matrix?.roles.map((role) => (
											<td key={role._id} className="py-2 px-2">
												<div className="flex flex-wrap justify-center gap-1">
													{feature.actions.map((action) => {
														const checked = hasAction(
															role._id,
															featureCode,
															action,
														);
														return (
															<label
																key={action}
																className="flex items-center gap-0.5 cursor-pointer"
																title={`${action} - Click to toggle`}
															>
																<Checkbox
																	checked={checked}
																	onCheckedChange={() =>
																		handleToggle(role._id, featureCode, action)
																	}
																	disabled={toggleMutation.isPending}
																	className={checked ? actionColors[action] : ""}
																/>
																<span
																	className={`text-[10px] font-medium uppercase ${actionColors[action]}`}
																>
																	{action.charAt(0)}
																</span>
															</label>
														);
													})}
												</div>
											</td>
										))}
									</tr>
								);
							})}
						</tbody>
					</table>

					{filteredFeatures.length === 0 && (
						<div className="text-center py-12 text-muted-foreground">
							No features match your search
						</div>
					)}
				</CardContent>
			</Card>

			{/* Legend */}
			<Card>
				<CardContent className="pt-4">
					<div className="flex flex-wrap gap-6 text-sm">
						{Object.entries(actionColors).map(([action, color]) => (
							<div key={action} className="flex items-center gap-2">
								<Checkbox checked className={color} disabled />
								<span className={color}>{action.charAt(0).toUpperCase()}</span>
								<span className="text-muted-foreground">= {action}</span>
							</div>
						))}
					</div>
					<div className="flex gap-4 mt-4 pt-4 border-t text-xs text-muted-foreground">
						<div className="flex items-center gap-1">
							<Badge
								variant="outline"
								className="text-[10px] border-blue-300 text-blue-600"
							>
								<Link2 className="w-3 h-3 mr-1" />N
							</Badge>
							<span>= Requires N other features</span>
						</div>
						<div className="flex items-center gap-1">
							<Badge
								variant="outline"
								className="text-[10px] border-yellow-300 text-yellow-600"
							>
								<AlertTriangle className="w-3 h-3 mr-1" />N
							</Badge>
							<span>= N features depend on this</span>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Dependency Warning Dialog */}
			<DependencyWarningDialog
				open={dialogOpen}
				onOpenChange={setDialogOpen}
				mode={dialogMode}
				featureName={
					pendingToggle
						? dependencyCheck.getFeatureName(pendingToggle.featureCode)
						: ""
				}
				autoGrantedDependencies={autoGrantedDeps}
				affectedDependents={affectedDependents}
				onConfirm={handleDialogConfirm}
				onCancel={handleDialogCancel}
				isLoading={toggleMutation.isPending}
			/>
		</div>
	);
}
